import React, { useState } from 'react';
import { View, TouchableOpacity, ActivityIndicator, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faDownload, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import MyDataTable from '../../../components/MyDataTable';
import MyAppText from '../../../components/MyAppText';
import Toast from 'react-native-toast-message';
import { auditLogsService } from '../../../services/central-api/audit-logs';
import { LogsParamList } from '../../../typings/Params';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import ReportModal from './ReportModal';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { openExternalLinkInNewTab } from '../../../services/navigation-assistant';

export default function Reports() {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<LogsParamList, 'LogReports'>>();
    const navigation = useNavigation<NavigationProp<LogsParamList>>();

    // filters
    const [filters, setFilters] = useState<{ page: number; textFilter?: string; limit: number; }>({
        page: Math.max(route.params.page - 1, 0),
        textFilter: route.params.textFilter ?? '',
        limit: Number(route.params.limit) || 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isDownloading, setIsDownloading] = useState(false);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['logsGetReports', filters],
        queryFn: () => auditLogsService.getReports(filters),
        enabled: isFocused,
        refetchInterval: isFocused ? 15000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    function handleFilters() {
        setFilters({
            ...filters,
            textFilter: textFilter !== '' ? textFilter : undefined,
            page: 0
        });

        navigation.setParams({
            textFilter: textFilter !== '' ? textFilter : undefined,
            page: 1
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    return <Container>
        <Header
            title={translate('reports')}
            actions={<CustomButton icon={faDownload} text={translate('logsReport')} onPress={() => setModalVisible(true)} />}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredReports')}
                zIndex={2}
                filterInput={<FilterInput
                    value={textFilter}
                    onChange={text => {
                        setTextFilter(text);
                    }}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('name'),
                        selector: row => row.name,
                        wrap: true
                    },
                    {
                        name: translate('user'),
                        selector: row => row.user?.name || '',
                        wrap: true
                    },
                    {
                        name: translate('status'),
                        cell: row =>
                            <View style={{ flexDirection: 'row', columnGap: 5, alignItems: 'center', width: '100%' }}>
                                {row.status == 'pending' ?
                                    <ActivityIndicator size={16} color={theme.colors.field.text} /> :
                                    <FontAwesomeIcon style={row.status == 'success' ? styles.success : styles.failed} fontSize={16} icon={row.status == 'success' ? faCheckCircle : faTimesCircle}></FontAwesomeIcon>
                                }

                                <MyAppText style={row.status == 'success' ? styles.success : row.status == 'failed' ? styles.failed : {}}>{translate(row.status)}</MyAppText>
                            </View>
                    },
                    {
                        name: translate('filters'),
                        cell: row => <View style={{ width: '100%' }}>
                            <View style={{ flexDirection: 'column', rowGap: 5, paddingVertical: 5 }}>
                                <MyAppText>{translate('start')}: {moment(row.filters.startDate).format('DD/MM/YYYY')}</MyAppText>
                                <MyAppText>{translate('end')}: {moment(row.filters.endDate).format('DD/MM/YYYY')}</MyAppText>
                                <MyAppText>{translate('action')}: {row.filters.action ? translate(row.filters.action) : translate('all')}</MyAppText>
                                <MyAppText>{translate('user')}: {row.filters.user ? row.filters.user : translate('masculineAll')}</MyAppText>
                                <MyAppText>{translate('format')}: {row.filters.isPdf ? 'PDF' : 'XLSX'}</MyAppText>
                            </View>
                        </View>
                    },
                    {
                        name: translate('createdAt'),
                        selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                    },
                    {
                        name: translate('action'),
                        button: true,
                        cell: row =>
                            <TouchableOpacity disabled={isDownloading} onPress={async () => {
                                try {
                                    setIsDownloading(true);
                                    const video = await auditLogsService.getReport(row.id);
                                    openExternalLinkInNewTab(video.resultUrl);
                                } catch (error) {
                                    Toast.show({
                                        type: 'sentinelxError',
                                        text1: translate('unexpectedError'),
                                    });
                                } finally {
                                    setIsDownloading(false);
                                }
                            }}>
                                {row.status === 'success' ? <FontAwesomeIcon fontSize={16} color={theme.colors.iconColor} icon={faDownload}></FontAwesomeIcon> : null}
                            </TouchableOpacity>
                    }]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    totalItems={data?.count ?? 0}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>
        <ReportModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            onClose={() => {
                setModalVisible(false);
                refetch();
            }}
        />
    </Container>;
}

const styleSheet = createStyleSheet(theme => ({
    success: {
        color: theme.colors.successes['500'],
    },
    failed: {
        color: theme.colors.errors['500'],
    },
}));
